import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Star, Trophy, Crown, Gift, ArrowLeft, Gem } from 'lucide-react';
import { AppState } from '../../app/types';
import { GroupId } from '../../config/systemConfig';
import { useAppStore } from '../../app/hooks/useAppStore';
import { useDjaySelection, useVariantUnlocks, TOTAL_VARIANTS, useLongPressDiscovered, useDjayCustomization, useTreatmentUnlocks, TOTAL_TREATMENTS, usePersistUnlockedCosmetics, useAchievementCheckData } from '../../mascot';
import type { MascotRarity } from '../../mascot';
import { PageHero } from '../shared/PageHero';
import { PageContainer } from '../shared/PageContainer';
import { itemVariants } from '../shared/animations';
import { SectionHeader } from '../ui/SectionHeader';
import { StatTile } from '../ui/StatTile';
import { ACHIEVEMENTS, CATEGORY_LABELS, Achievement } from '../progress/AchievementsSection';
import { CollectionLocker } from './DjayShowcase';
import { AchievementCategoryGroup } from './AchievementCategoryGroup';
import { useCollectionProgress } from './useCollectionProgress';
interface CollectionPageProps {
  state: AppState;
  selectedGroup: GroupId;
  onNavigate?: (view: string) => void;
}
const RARITY_STYLES: Record<MascotRarity, {
  border: string;
  bg: string;
  text: string;
  glow: string;
  label: string;
}> = {
  common: {
    border: 'border-slate-200',
    bg: 'bg-slate-50',
    text: 'text-slate-600',
    glow: '',
    label: 'Gewoon'
  },
  uncommon: {
    border: 'border-emerald-300',
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    glow: 'shadow-[0_0_15px_rgba(52,211,153,0.3)]',
    label: 'Ongewoon'
  },
  rare: {
    border: 'border-amber-300',
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    glow: 'shadow-[0_0_15px_rgba(251,191,36,0.4)]',
    label: 'Zeldzaam'
  },
  legendary: {
    border: 'border-purple-400',
    bg: 'bg-purple-50',
    text: 'text-purple-700',
    glow: 'shadow-[0_0_20px_rgba(167,139,250,0.5)]',
    label: 'Legendarisch'
  },
  secret: {
    border: 'border-rose-400',
    bg: 'bg-rose-50',
    text: 'text-rose-700',
    glow: 'shadow-[0_0_20px_rgba(244,63,94,0.5)]',
    label: 'Geheim'
  }
};
export function CollectionPage({
  state,
  selectedGroup,
  onNavigate
}: CollectionPageProps) {
  const {
    getModuleMasteryForGroup,
    getAnsweredQuestions,
    markCosmeticUnlocked
  } = useAppStore();
  const {
    selectedVariant,
    setSelectedVariant,
    selectedAccessory,
    setSelectedAccessory,
    selectedTier,
    setSelectedTier,
    selectedTreatment,
    setSelectedTreatment
  } = useDjaySelection();
  // Long-press discovery now derived from the easter-egg registry
  // (audit S4, ronde 3 2026).
  const hasDiscoveredLongPress = useLongPressDiscovered();
  // Achievement-gate input assembled by a mascot-domain selector — the inline
  // totalQuestionsAnswered / masteredCount / streak-field mapping that used to
  // live here now lives in one named domain concern (useAchievementCheckData).
  const checkData = useAchievementCheckData(state, getAnsweredQuestions, getModuleMasteryForGroup);
  const [focusedVariant, setFocusedVariant] = useState<string | null>(null);
  useEffect(() => {
    const focusId = sessionStorage.getItem('collectionFocus');
    if (focusId) {
      sessionStorage.removeItem('collectionFocus');
      setFocusedVariant(focusId);
      setTimeout(() => {
        document.getElementById(`variant-${focusId}`)?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 100);
      setTimeout(() => setFocusedVariant(null), 3000);
    }
  }, []);
  // --- Mascot Variants (single source of truth) ---
  const {
    variants: mascotVariants,
    unlockedCount: unlockedVariantsCount
  } = useVariantUnlocks(hasDiscoveredLongPress);
  const unlockedCosmetics = useMemo(() => state.stats.unlockedCosmetics ?? [], [state.stats.unlockedCosmetics]);
  const {
    accessories,
    tiers,
    unlockedAccessoriesCount,
    unlockedTiersCount
  } = useDjayCustomization(checkData, unlockedCosmetics);
  // --- Treatment finishes (usage-earned rarity finishes) ---
  const {
    treatments,
    unlockedCount: unlockedTreatmentsCount
  } = useTreatmentUnlocks(checkData, unlockedCosmetics);
  // Persist standalone cosmetics + treatment finishes the moment their gate is
  // first met. Domain-owned side-effect (drained out of this page body).
  usePersistUnlockedCosmetics(checkData, unlockedCosmetics, markCosmeticUnlocked);
  const achievementsWithProgress = useMemo(() => {
    return ACHIEVEMENTS.map(achievement => ({
      ...achievement,
      isUnlocked: achievement.check(checkData),
      progress: achievement.getProgress ? achievement.getProgress(checkData) : undefined
    }));
  }, [checkData]);
  // Combined collectible progress + next-reward teaser are derived in a
  // dedicated collection-domain selector (de-dups achievement-owned variants
  // vs. standalone ones, and owns the achievement tally). The two per-track
  // StatTiles below still show full per-track tallies.
  const {
    totalUnlocked,
    totalCollectibles,
    progressPercentage,
    nextReward,
    unlockedAchievementsCount,
    totalAchievementsCount
  } = useCollectionProgress(mascotVariants, achievementsWithProgress);
  const focusVariant = (variantId: string) => {
    setFocusedVariant(variantId);
    setTimeout(() => {
      document.getElementById(`variant-${variantId}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }, 50);
    setTimeout(() => setFocusedVariant(null), 3000);
  };
  return <PageContainer className="pb-20" data-id="element-1113" data-mp="1yj2x5rdfnmx5">
      <motion.div variants={itemVariants} className="mb-4" data-id="element-1114" data-mp="1yk4c46xkktk5">
        {onNavigate && <button onClick={() => onNavigate('progress')} className="flex items-center gap-2 text-slate-500 hover:text-slate-800 font-bold text-sm transition-colors" data-id="element-1115" data-mp="1ozujijydl6c">
            <ArrowLeft className="w-4 h-4" data-id="element-1116" data-mp="1r6myzrgo8a8i" />
            Terug naar Voortgang
          </button>}
      </motion.div>
      <motion.div variants={itemVariants} data-id="element-1117" data-mp="1w07y5mu03a9a">
        <PageHero title="Mijn Collectie" subtitle="Verdien prestaties door te oefenen en speel unieke Djay-looks vrij!" gradientClasses="from-amber-500 via-orange-500 to-rose-500" djayEmotion="excited" djayVariant={selectedVariant} djayAccessory={selectedAccessory} djayTier={selectedTier} djayTreatment={selectedTreatment} icon={Sparkles} iconBgClass="bg-amber-400" floatingEmojis={['✨', '🏆', '⭐']} data-id="element-1118" data-mp="1h9qvy19jkgbc" />
      </motion.div>

      <motion.div variants={itemVariants} className="mt-8 space-y-8" data-id="element-1119" data-mp="24x8inidy8o7g">
        {/* Overall Progress Card — ring-led header, then a tidy 3-up stat grid
            separated from the summary so the page reads as "one headline + its
            breakdown" instead of a crowded chip cluster. */}
        <div className="bg-white rounded-2xl border-2 border-slate-100 shadow-sm overflow-hidden" data-id="element-1120" data-mp="lykcald8hxna">
          <div className="flex items-center gap-4 p-5 sm:p-6" data-id="element-1121" data-mp="h0itm1jq00cc">
            <div className="relative w-16 h-16 flex-shrink-0" data-id="element-1122" data-mp="vyuqa873f93i">
              <svg className="w-16 h-16 transform -rotate-90" data-id="element-1123" data-mp="vvytwoaw8sh6">
                <circle cx="32" cy="32" r="28" fill="none" stroke="#F1F5F9" strokeWidth="6" data-id="element-1124" data-mp="r0jgfxgssyla" />
                <circle cx="32" cy="32" r="28" fill="none" stroke="#F59E0B" strokeWidth="6" strokeDasharray="175.9" strokeDashoffset={175.9 - progressPercentage / 100 * 175.9} strokeLinecap="round" className="transition-all duration-1000 ease-out" data-id="element-1125" data-mp="3uhrwcbrytodd" />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center" data-id="element-1126" data-mp="2q75y8ndk647s">
                <Crown className="w-6 h-6 text-amber-500" data-id="element-1127" data-mp="2v4pfzx9gsz4h" />
              </div>
            </div>
            <div className="min-w-0" data-id="element-1128" data-mp="303sib9y28r8r">
              <h2 className="text-xl font-black text-slate-800 leading-tight" data-id="element-1129" data-mp="302qx4cnbyodb">
                Verzameling compleet
              </h2>
              <p className="text-sm text-slate-500 font-medium" data-id="element-1130" data-mp="39505avv4m5nj">
                {totalUnlocked} van de {totalCollectibles} behaald ·{' '}
                <span className="font-bold text-amber-600" data-id="element-1131" data-mp="3e31nzfon42yh">
                  {progressPercentage}%
                </span>
              </p>
            </div>
          </div>

          <div className="grid grid-cols-3 divide-x divide-slate-100 border-t-2 border-slate-100 bg-slate-50/60" data-id="element-1132" data-mp="3ntmudpak2bry">
            <StatTile icon={Trophy} iconColorClass="text-amber-500" label="Prestaties" value={`${unlockedAchievementsCount}/${totalAchievementsCount}`} progress={unlockedAchievementsCount / totalAchievementsCount * 100} progressColorClass="bg-amber-500" className="!p-3 sm:!p-4" data-id="element-1133" data-mp="3rw9fz8gmv0gp" />
            <StatTile icon={Star} iconColorClass="text-orange-500" label="Varianten" value={`${unlockedVariantsCount}/${TOTAL_VARIANTS}`} progress={unlockedVariantsCount / TOTAL_VARIANTS * 100} progressColorClass="bg-orange-500" className="!p-3 sm:!p-4" data-id="element-1134" data-mp="1c7cdt163xiby" />
            <StatTile icon={Gem} iconColorClass="text-purple-500" label="Finishes" value={`${unlockedTreatmentsCount}/${TOTAL_TREATMENTS}`} progress={unlockedTreatmentsCount / TOTAL_TREATMENTS * 100} progressColorClass="bg-purple-500" className="!p-3 sm:!p-4" data-id="element-1135" data-mp="1v4vk3x16kmuo" />
          </div>
        </div>

        {/* Next reward teaser — mirrors the Voortgang GhostTrophy so the
            growth ↔ collection loop reads the same in both directions. */}
        {nextReward && <motion.button type="button" onClick={() => nextReward.variantId && focusVariant(nextReward.variantId)} whileHover={nextReward.variantId ? {
        scale: 1.01
      } : undefined} whileTap={nextReward.variantId ? {
        scale: 0.99
      } : undefined} className={`w-full flex items-center gap-4 p-4 bg-amber-50 border-2 border-amber-200 rounded-2xl text-left transition-shadow ${nextReward.variantId ? 'hover:shadow-md cursor-pointer' : 'cursor-default'}`} aria-label={nextReward.variantId ? `Volgende beloning: ${nextReward.name}. Bekijk in je collectie.` : `Volgende beloning: ${nextReward.name}.`} data-id="element-1136" data-mp="1ziqe6p6kt8a1">
            <div className="w-12 h-12 flex-shrink-0 rounded-full bg-amber-100 text-amber-500 flex items-center justify-center" data-id="element-1137" data-mp="3bsnxmhl6kmgb">
              <Gift className="w-6 h-6" data-id="element-1138" data-mp="2xvr94msjbh2l" />
            </div>
            <div className="flex-1 min-w-0" data-id="element-1139" data-mp="31toypqq7w56i">
              <p className="text-[10px] sm:text-xs font-bold text-amber-500 uppercase tracking-wider flex items-center gap-1" data-id="element-1140" data-mp="2o3lhje6731nx">
                <Sparkles className="w-3 h-3" data-id="element-1141" data-mp="2sula678a6xkm" /> Volgende beloning
              </p>
              <h3 className="text-sm sm:text-base font-black text-slate-800 leading-tight truncate" data-id="element-1142" data-mp="2e5p3adhynqll">
                {nextReward.name}
              </h3>
              <div className="mt-1.5 flex items-center gap-2" data-id="element-1143" data-mp="2j4s1pf3fqykl">
                <div className="h-2 flex-1 bg-amber-100 rounded-full overflow-hidden" data-id="element-1144" data-mp="254hbme9y9o0h">
                  <motion.div className="h-full bg-amber-400 rounded-full" initial={{
                width: 0
              }} animate={{
                width: `${Math.round(nextReward.pct * 100)}%`
              }} transition={{
                duration: 0.6,
                ease: 'easeOut'
              }} data-id="element-1145" data-mp="2a4by85mozrfq" />
                </div>
                <span className="text-[11px] font-bold text-slate-500 flex-shrink-0" data-id="element-1146" data-mp="ymhfteqk2uf7">
                  {nextReward.current}/{nextReward.max} {nextReward.label}
                </span>
              </div>
            </div>
          </motion.button>}

        {/* Interactive Djay locker (look picker) */}
        <CollectionLocker variants={mascotVariants} selectedVariant={selectedVariant} onSelectVariant={setSelectedVariant} accessories={accessories} selectedAccessory={selectedAccessory} onSelectAccessory={setSelectedAccessory} tiers={tiers} selectedTier={selectedTier} onSelectTier={setSelectedTier} treatments={treatments} selectedTreatment={selectedTreatment} onSelectTreatment={setSelectedTreatment} focusedVariant={focusedVariant} rarityStyles={RARITY_STYLES} data-id="element-1147" data-mp="kmykjmiufdkb" />

        {/* Prestaties Section */}
        <div className="space-y-8 pt-4" data-id="element-1148" data-mp="1ynusd6oerjrb">
          <SectionHeader title="Prestaties" icon={Trophy} accentColor="bg-amber-100 text-amber-600" data-id="element-1149" data-mp="23lms2mvaoa62" />

          <div className="space-y-12" data-id="element-1150" data-mp="28iwdlrfkc1om">
            {(Object.keys(CATEGORY_LABELS) as Achievement['category'][]).map(category => {
            const categoryAchievements = achievementsWithProgress.filter(a => a.category === category);
            if (categoryAchievements.length === 0) return null;
            return <AchievementCategoryGroup key={category} title={CATEGORY_LABELS[category]} achievements={categoryAchievements} mascotVariants={mascotVariants} data-id="element-1151" data-mp="n7wbwhqwhvg2" />;
          })}
          </div>
        </div>
      </motion.div>
    </PageContainer>;
}